<script lang="ts" setup>import { computed as _computed } from 'vue';

import type { Attachment, GreetingCardStorySlide } from '@consumer/types/serializers'
import Cover from './Cover.vue'
import NavBar from './NavBar.vue'
import NotePage from './NotePage.vue'
import PhotoPage from './PhotoPage.vue'
import ProgressBar from './ProgressBar.vue'
import VoiceMemoPage from './VoiceMemoPage.vue'

// 'full-page' is the leading beat for cards that ship a full-page face: it
// takes the place of the book cover, filling the screen instead of flipping.
type PageKind = 'full-page' | 'note' | 'voice-memo' | 'photo'
interface BookPage {
  kind: PageKind
  slideIndex?: number
}

withDefaults(defineProps<{
  storySlides: GreetingCardStorySlide[]
  voiceMemo: Attachment | string | null | undefined
  messageForRecipient?: string
  previewImageUrl?: string | null
  fullPageImageUrl?: string | null
  senderName?: string
  // Label for the final page's CTA. The recipient flow keeps "Open Your Gift";
  // the demo showcase overrides it to "Open Gift" (per its design) and the
  // buyer preview to "Close Preview" (there is no gift to open there).
  openGiftLabel?: string
}>(), { openGiftLabel: 'Open Your Gift', })

const emit = defineEmits<{
  redeem: []
  backToEnvelope: []
  // Current reading beat, so a guided host (the demo/preview showcase) can
  // sync its narration to whichever page is showing. The recipient page
  // ignores it. Keyed off the page kind, not a fixed index, so it follows
  // whatever pages actually exist (voice memo / photos are conditional).
  beat: [ReadingBeat]
}>()

export type ReadingBeat = 'cover' | 'note' | 'voice-memo' | 'photo'

const hasVoiceMemo = _computed(() => Boolean(__props.voiceMemo))

// Cards with a full-page face drop the book-flip entirely: the face fills the
// card leaving the envelope, then expands to fill the screen (see onMounted).
const isFullPage = _computed(() => Boolean(__props.fullPageImageUrl))

// The note page is only included when the sender actually wrote a
// message — otherwise `NotePage` renders an empty screen behind the
// opened cover (a card with no note left the recipient staring at a
// blank page). When no page qualifies (no note, voice memo, or photos)
// the cover opens straight into redemption instead of an empty strip.
// A full-page card leads with its face as the first beat.
const pages = _computed<BookPage[]>(() => {
  const list: BookPage[] = []
  if (isFullPage.value) list.push({ kind: 'full-page' })
  if (__props.messageForRecipient) list.push({ kind: 'note' })
  if (hasVoiceMemo.value) list.push({ kind: 'voice-memo' })
  __props.storySlides.forEach((_, index) => list.push({ kind: 'photo', slideIndex: index }))
  return list
})

// ===== Gesture-driven cover flip + page stack =====
//
// Two sub-phases:
//   1. Cover flip: coverProgress goes 0 → 1 via horizontal drag. Simultaneously
//      rotates the cover open and expands the underlying first page to fullscreen.
//   2. Page navigation: once coverProgress = 1, the user is reading the first
//      page. Horizontal swipes navigate through pages (carousel-style: pages
//      laid out in a strip, swiping shifts the strip).

const coverProgress = ref(0)          // 0 (closed) → 1 (fully open)
const pageIndex = ref(0)              // 0 = first content page, 1 = next, etc.
const pageOffset = ref(0)             // horizontal drag offset during page swipe, in px
const isDragging = ref(false)
const isSnapping = ref(false)         // true while the snap-back animation is running

// Emit the current reading beat so a guided host (the demo/preview showcase)
// can sync its narration. Declared after coverProgress/pageIndex so the
// `immediate` watch can read them at setup without hitting the TDZ.
const currentBeat = _computed<ReadingBeat>(() => {
  if (coverProgress.value < 1) return 'cover'
  const kind = pages.value[pageIndex.value]?.kind
  // The full-page face stands in for the cover beat (same narration slot).
  return kind === 'full-page' ? 'cover' : kind ?? 'note'
})
watch(() => currentBeat.value, beat => emit('beat', beat), { immediate: true })

// Full-page cards never flip: once the reading stage takes over from the
// envelope (the face already fills the card), expand it to fill the screen by
// snapping --cover-progress 0 → 1. That reuses the same growth the flip drives
// on the first page, with the snap transition making it a smooth expand.
onMounted(() => {
  if (!isFullPage.value) return
  // Double rAF: let the card-size frame (coverProgress 0) paint first, so the
  // snap transition animates the 0 → 1 growth instead of jumping straight to
  // fullscreen.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    isSnapping.value = true
    coverProgress.value = 1
    setTimeout(() => { isSnapping.value = false }, 400)
  }))
})

let dragStartX = 0
let dragStartY = 0
let dragStartProgress = 0
let dragStartOffset = 0
// 'cover'     = adjusts cover-flip progress (open ↔ closed)
// 'page'      = horizontal page navigation (next/prev)
// 'undecided' = waiting for the first significant pointer movement to
//               classify the gesture
// 'vertical'  = vertical-dominant gesture — we step out of the way so
//               native scroll on overflow children (e.g. a long note)
//               handles it. `touch-action: pan-y` on `.reading-stage`
//               lets the browser take over once we ignore the move.
type DragMode = 'cover' | 'page' | 'undecided' | 'vertical'
let dragMode: DragMode = 'undecided'
const DIRECTION_THRESHOLD_PX = 5

function onPointerDown (e: PointerEvent) {
  if (isSnapping.value) return
  dragStartX = e.clientX
  dragStartY = e.clientY
  isDragging.value = true
  dragStartProgress = coverProgress.value
  dragStartOffset = pageOffset.value
  // Always start undecided — the direction check in `onPointerMove`
  // picks vertical / cover / page once the user has moved enough to
  // make their intent clear. Without this, a vertical scroll on a
  // long note that drifts even a pixel sideways immediately becomes
  // a cover-close drag, which fades the text out and back in.
  dragMode = 'undecided'
  ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
}

function onPointerMove (e: PointerEvent) {
  if (!isDragging.value) return
  const dx = e.clientX - dragStartX
  const dy = e.clientY - dragStartY
  const w = window.innerWidth
  if (dragMode === 'undecided') {
    if (Math.abs(dx) < DIRECTION_THRESHOLD_PX && Math.abs(dy) < DIRECTION_THRESHOLD_PX) return
    // Vertical-dominant → hand off to native scroll (no-op here).
    if (Math.abs(dy) > Math.abs(dx)) {
      dragMode = 'vertical'
    }
    // Full-page cards have no flippable cover — every horizontal drag just
    // navigates the page strip.
    else if (isFullPage.value) {
      dragMode = 'page'
    }
    else if (coverProgress.value < 1) {
      // Cover not yet open — any horizontal drives the cover flip.
      dragMode = 'cover'
    }
    else if (pageIndex.value === 0) {
      // First page with cover open: left = next page, right = close cover.
      dragMode = dx < 0 ? 'page' : 'cover'
    }
    else {
      dragMode = 'page'
    }
  }
  if (dragMode === 'vertical') return
  if (dragMode === 'cover') {
    const next = dragStartProgress + (-dx / w)
    coverProgress.value = Math.max(0, Math.min(1, next))
  }
  else if (dragMode === 'page') {
    pageOffset.value = dragStartOffset + dx
  }
}

function onPointerUp () {
  if (!isDragging.value) return
  isDragging.value = false
  isSnapping.value = true
  if (dragMode === 'cover') {
    // Opening the cover on a card with no readable pages redeems
    // directly; keep the cover closed so it stays visible while the
    // stage swipes out instead of flashing an empty page.
    if (coverProgress.value >= 0.5 && !pages.value.length) {
      coverProgress.value = 0
      emit('redeem')
    }
    else {
      coverProgress.value = coverProgress.value >= 0.5 ? 1 : 0
    }
  }
  else if (dragMode === 'page') {
    const w = window.innerWidth
    const threshold = w * 0.25
    if (pageOffset.value <= -threshold) {
      if (pageIndex.value < pages.value.length - 1) {
        pageIndex.value += 1
      }
      else {
        // Swiping left past the final page triggers the redemption finale.
        emit('redeem')
      }
    }
    else if (pageOffset.value >= threshold && pageIndex.value > 0) {
      pageIndex.value -= 1
    }
    pageOffset.value = 0
  }
  setTimeout(() => { isSnapping.value = false }, 400)
}

function onPointerCancel () {
  if (!isDragging.value) return
  isDragging.value = false
  isSnapping.value = true
  if (dragMode === 'cover') coverProgress.value = dragStartProgress >= 0.5 ? 1 : 0
  else if (dragMode === 'page') pageOffset.value = 0
  setTimeout(() => { isSnapping.value = false }, 400)
}

// ===== Navigation buttons (mirror the swipe gesture) =====

// The descriptive "reveal the next thing" label used when leaving a
// cover-like beat (the book cover, or a full-page face) for the first
// readable page.
function firstReadLabel (page?: BookPage) {
  if (!page) return __props.openGiftLabel
  if (page.kind === 'voice-memo') return 'Listen To Memo'
  if (page.kind === 'photo') return 'See Photos'
  return 'Read Message'
}

const nextButtonLabel = _computed(() => {
  const current = pages.value[pageIndex.value]
  // Still on a cover-like beat (book cover mid-flip, or the full-page face):
  // the first readable page is what the button reveals next.
  if (coverProgress.value < 1 || current?.kind === 'full-page') {
    return firstReadLabel(pages.value[pageIndex.value + (isFullPage.value ? 1 : 0)])
  }
  const next = pages.value[pageIndex.value + 1]
  if (!next) return __props.openGiftLabel
  if (next.kind === 'voice-memo') return 'Listen To Memo'
  if (next.kind === 'photo') {
    return current?.kind === 'photo' ? 'Next Photo' : 'See Photos'
  }
  return 'Next'
})

// Always true — the NavBar's back button doubles as a back-to-envelope
// affordance on the very first state (pageIndex 0, cover not yet
// opened). `goBack` below decides what to do based on the current
// position in the page stack.
const canGoBack = _computed(() => true)

// Fractional "current page" for the ProgressBar — `pageIndex` plus
// however far the user has dragged the strip toward the next/prev
// page. Drives the per-dash fill so the active dash grows smoothly
// across the swipe instead of snapping at the threshold.
const progressBarCurrent = _computed(() => {
  const w = typeof window === 'undefined' ? 1 : (window.innerWidth || 1)
  return pageIndex.value - pageOffset.value / w
})

function goNext () {
  if (isSnapping.value) return
  if (coverProgress.value < 1) {
    // Nothing to read behind the cover — go straight to redemption
    // rather than flipping open onto an empty page.
    if (!pages.value.length) {
      emit('redeem')
      return
    }
    isSnapping.value = true
    coverProgress.value = 1
  }
  else if (pageIndex.value < pages.value.length - 1) {
    isSnapping.value = true
    pageIndex.value += 1
  }
  else {
    // Last page → "Open Your Gift" triggers the redemption finale.
    emit('redeem')
    return
  }
  setTimeout(() => { isSnapping.value = false }, 400)
}

function goBack () {
  if (isSnapping.value) return
  if (pageIndex.value > 0) {
    isSnapping.value = true
    pageIndex.value -= 1
  }
  else if (!isFullPage.value && coverProgress.value > 0) {
    isSnapping.value = true
    coverProgress.value = 0
  }
  else {
    // First page, cover not yet opened — the only "back" left is out of
    // the reading stage entirely. StaticAnimatedPage listens to this and
    // resets to its 'idle' (envelope) state.
    emit('backToEnvelope')
    return
  }
  setTimeout(() => { isSnapping.value = false }, 400)
}
</script>

<template>
  <div
    class="reading-stage"
    :class="{ snapping: isSnapping }"
    :style="{
      '--cover-progress': coverProgress,
      '--page-offset': `${pageOffset}px`,

      // Binary flag — flips 0 → 1 only once the cover has fully
      // opened. Page contents (note text, photo caption) bind their
      // opacity to this with a delayed transition so they fade in
      // *after* the open animation completes instead of growing along
      // with the cover.
      '--cover-fully-open': coverProgress >= 1 ? 1 : 0,

      // Binary flag — flips 0 → 1 only once the cover has fully
      // closed. Page contents (note text, photo caption) bind their
      // opacity to this with a delayed transition so they fade in
      // *after* the close animation completes instead of shrinking along
      // with the cover.
      '--cover-fully-closed': coverProgress <= 0 ? 1 : 0,

      // Direction-aware transition for the text opacity above. When
      // the cover finishes opening, the text waits a beat then fades
      // in (0.3s delay, 0.4s fade). When the cover starts closing,
      // the fade-out also waits 200ms before starting — that pause
      // is what makes a tiny horizontal slip during a vertical scroll
      // (cover-progress dips below 1 for a few frames then snaps back)
      // a no-op visually: the user releases, --cover-fully-open flips
      // back to 1 before the close transition's delay completes, so
      // the opacity never actually animates.
      '--cover-fully-open-transition':
        coverProgress >= 1
          ? 'opacity 0.4s ease-out 0.3s'
          : 'opacity 0s',
      // ProgressBar dash fill transition — set only while a snap is
      // in flight so the bars smoothly catch up to the post-release
      // pageIndex. During an active drag we leave it at `none` so the
      // fill tracks the finger at 1:1 with no perceptible lag.
      '--progress-fill-transition': isSnapping
        ? 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        : 'none',
    }"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
  >
    <!-- Page strip (note, voice memo, photos). Each page is positioned by
         its index offset from the current page (--page-distance) so swiping
         shifts the whole strip and only the genuinely adjacent page comes
         into view. -->
    <div
      v-for="(page, index) in pages"
      :key="`page-${index}-${page.kind}-${page.slideIndex ?? ''}`"
      class="reading-page"
      :class="{
        'reading-page-flipping': index === 0 && pageIndex === 0,
        'reading-page-full': page.kind === 'full-page',
      }"
      :style="{ '--page-distance': index - pageIndex }"
    >
      <img
        v-if="page.kind === 'full-page' && fullPageImageUrl"
        :src="fullPageImageUrl"
        alt=""
        class="reading-page-full-image"
      >
      <NotePage v-else-if="page.kind === 'note'" :message="messageForRecipient"/>
      <VoiceMemoPage
        v-else-if="page.kind === 'voice-memo'"
        :voiceMemo="voiceMemo"
        :active="pageIndex === index"
      />
      <PhotoPage
        v-else-if="page.kind === 'photo' && page.slideIndex !== undefined && storySlides[page.slideIndex]"
        :slide="storySlides[page.slideIndex]"
      />
    </div>

    <!-- The flippable book cover — only for cards without a full-page face,
         which lead with the face as their first page instead. -->
    <Cover
      v-if="!isFullPage && pageIndex === 0"
      :previewImageUrl="previewImageUrl"
      :senderName="senderName"
    />

    <!-- Stories-style page progress bar pinned at the top. One dash per
         page (note + optional voice memo + each photo). -->
    <!--
      Progress bar is hidden until the cover has finished opening
      (coverProgress = 1). Showing it while the cover is still on the
      first page would be premature — the recipient hasn't started
      reading the note yet.
    -->
    <ProgressBar
      v-if="coverProgress >= 1"
      :total="pages.length"
      :current="progressBarCurrent"
    />

    <NavBar
      :nextLabel="nextButtonLabel"
      :canGoBack="canGoBack"
      @next="goNext"
      @back="goBack"
    />
  </div>
</template>

<style scoped lang="scss">
// Reading stage: gesture-driven cover flip + page stack.
// All position/size interpolations are driven by two JS-set CSS variables:
//   --cover-progress: 0 (closed) → 1 (fully open). Controls cover rotation
//     AND the first page's growth from card-size to fullscreen.
//   --page-offset: horizontal drag offset in px for the current page during
//     subsequent swipe navigation.
.reading-stage {
  height: 100%;
  inset: 0;
  perspective: 1500px;
  position: absolute;

  // `pan-y` lets the browser handle vertical pans natively — so a long
  // note inside `.page-note-message { overflow-y: auto }` actually
  // scrolls under the user's finger. Horizontal pans still come through
  // as pointer events for our cover/page drag logic, and `onPointerMove`
  // routes vertical-dominant gestures to a no-op `'vertical'` drag mode
  // so we never compete with the native scroll.
  touch-action: pan-y;
  user-select: none;
  width: 100%;
}

// When not dragging, the snap-back/forward animation runs. `top` is
// in the transition list because the cover-closed → open animation
// also drives a vertical lift via `top` (so the card sits midway
// between page top and navbar top while closed, returns to true
// center when fullscreen). Keeping it on `top` rather than rolled
// into `transform` is important on iOS — the cover has `rotateY` in
// its transform, the page doesn't, and iOS interpolates the two
// transforms on subtly different timelines when calc-with-var Y
// values are mixed in. Splitting to `top` makes both layers go
// through the same interpolator.
.reading-stage.snapping .reading-page,
.reading-stage.snapping :deep(.cover) {
  transition:
    top 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    border-radius 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

// Pages laid out in a horizontal strip. Each page sits at its own offset from
// the current page (via --page-distance: index - pageIndex). Swiping shifts
// the entire strip by --page-offset, so adjacent pages slide in from the
// sides like a carousel and there's no overlap.
.reading-page {
  // --page-bg-color is sampled from the (1,1) pixel of the greeting card
  // image (see useImagePixelColor). Falls back to the default card color
  // when sampling isn't available.
  background: var(--page-bg-color, var(--card-color));
  height: 100dvh;
  left: 50%;
  overflow: hidden;
  position: absolute;
  top: 50%;
  transform:
    translate(
      calc(-50% + var(--page-distance) * 100vw + var(--page-offset)),
      -50%
    );
  width: 100vw;
}

// Bottom-of-page fade: transparent → --page-bg-color so any page content
// that bleeds toward the bottom (e.g. PhotoPage's blurred background)
// dissolves cleanly into the solid page color before the NavBar. Applied
// to .reading-page so all three pages get it for free; pointer-events:
// none keeps it from intercepting taps on the NavBar above.
.reading-page::after {
  background: linear-gradient(to bottom, transparent, var(--page-bg-color, var(--card-color)));
  bottom: 0;
  content: "";
  height: 50dvh;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  z-index: 2;
}

// The full-page face is meant to be seen edge-to-edge, so it skips the
// bottom-of-page fade the readable pages use to dissolve into the NavBar.
.reading-page-full::after {
  content: none;
}

// Fills the page at every size — so it fills the card as it leaves the
// envelope and stays full-bleed as the page expands to fill the screen,
// revealing more of the image as it grows.
.reading-page-full-image {
  height: 100%;
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 100%;
}

// Starting dimensions for the first page in its CLOSED state, matched to
// the envelope card's rendered visual size. The card now sits at scene
// center (no translateY offset), so the first page starts there too.
$start-width: calc(var(--card-width) * var(--scene-scale));
$start-height: calc(var(--card-height) * var(--scene-scale));

// The first page during the cover-flip phase (pageIndex === 0): grows from
// card size to fullscreen as --cover-progress goes 0 → 1.
.reading-page-flipping {
  align-items: center;
  display: flex;
  height: calc(#{$start-height} + (100dvh - #{$start-height}) * var(--cover-progress) - (var(--cover-fully-closed) * 1px));
  justify-content: center;

  // Lift via `top` rather than baking it into the `transform` calc — see the
  // `.snapping` transition rule above for why. Centers the closed card in the
  // band between the narration reserve (top) and the navbar reserve (bottom) so
  // it lines up with the envelope handoff, retracting to true center as
  // `--cover-progress` reaches 1. --narration-reserved-space defaults to 0 (the
  // recipient flow has no narration), so only the demo/preview showcase shifts.
  top: calc(50% + (var(--narration-reserved-space, 0px) - var(--nav-bar-reserved-space, 0px)) / 2 * (1 - var(--cover-progress)));

  // `translate3d` (not `translate`) keeps the page on the same
  // GPU-composited path as `.cover` (which has rotateY → auto-3D).
  transform:
    translate3d(
      calc(-50% + var(--page-distance) * 100vw + var(--page-offset)),
      -50%,
      0
    );
  width: calc(#{$start-width} + (100vw - #{$start-width}) * var(--cover-progress) - (var(--cover-fully-closed) * 1px));
  will-change: top, transform, width, height;
  z-index: 5;
}
</style>
